<template>
    <v-container>
        <v-sheet>
            <v-row class="main-container" justify="space-around">
                <v-overlay :value="loading">
                    <v-progress-circular indeterminate size="64"></v-progress-circular>
                </v-overlay>
                <v-col justify="space-around" v-if="!showError && !showSuccess">
                    <v-sheet class="pa-8 rounded" elevation="18">
                        <h1 class="title">{{
                                `Welcome to the onboarding${company.name ? `, ${company.name}` : ''}!`
                            }}</h1>
                        <p>Thank you for starting to use our service. We have pre-filled your company details and all
                            you have to do is sign the contract. You must choose person down below who's going to sign
                            the contract. After that you need to authenticate yourself. Once you have signed the
                            contract, the service is open to you and you can start using Tarkpay. For further questions
                            please contact Tarkpay administrator.</p>
                        <v-form v-model="companyValidation">
                            <v-text-field
                                    v-model="company.name"
                                    :label="'Company name' | t"
                                    :rules="[v => !!v || this.t('Company name is required')]"
                                    prepend-icon="domain"
                                    :disabled="!notFilledFields.includes('name')"
                            />
                            <v-text-field
                                    v-model="company.identification_code"
                                    :label="'Company identification code' | t"
                                    :rules="[v => !!v || this.t('Identification code is required')]"
                                    prepend-icon="description"
                                    :readonly="companyInputsDisabled"
                                    :disabled="companyInputsDisabled"
                            />
                            <v-text-field
                                    v-model="company.address"
                                    :label="'Company address' | t"
                                    :rules="[v => !!v || this.t('Address is required')]"
                                    prepend-icon="map"
                                    :disabled="!notFilledFields.includes('address')"
                            />
                            <v-select
                                    v-model="company.country"
                                    :label="'Company address' | t"
                                    :rules="[v => !!v || this.t('Country is required')]"
                                    prepend-icon="flag"
                                    :items="countries"
                                    item-text="text"
                                    item-value="value"
                                    :disabled="!notFilledFields.includes('country')"
                            />
                            <v-text-field
                                    v-model="company.field_of_activity"
                                    :label="'Company\'s field of activity' | t"
                                    :rules="[v => !!v || this.t('Field of activity is required')]"
                                    prepend-icon="info_outline"
                                    :disabled="!notFilledFields.includes('field_of_activity')"
                            />
                            <v-text-field
                                    v-model="company.email"
                                    :label="'Company\'s email' | t"
                                    :rules="[v => !!v || this.t('Email is required')]"
                                    prepend-icon="email"
                                    :disabled="!notFilledFields.includes('email')"
                            />
                            <v-text-field
                                    v-model="company.phone_nr"
                                    :label="'Company\'s phone number' | t"
                                    :rules="[v => !!v || this.t('Phone number is required')]"
                                    prepend-icon="phone"
                                    :disabled="!notFilledFields.includes('phone_nr')"
                            />

                        </v-form>
                    </v-sheet>
                    <v-sheet class="pa-8 mt-3 rounded" elevation="18">
                        <v-row>
                            <v-col>
                                <h2 class="title">{{ "Related members" | t }}</h2>
                            </v-col>
                        </v-row>
                        <v-row>
                            <v-col cols="6">
                                <h2 class="subtitle">{{ "Members of the Board" | t }}</h2>
                                <v-col v-for="member in membersOfTheBoard" :key="`memberOfTheBoard_${member.id}`">
                                    <v-icon>person</v-icon>
                                    {{
                                        member.name
                                    }}{{ member.identification_code ? ` (${member.identification_code})` : '' }}
                                </v-col>
                            </v-col>
                            <v-col cols="6">
                                <h2 class="subtitle">{{ "Beneficial owners" | t }}</h2>
                                <v-col v-for="member in beneficialOwners" :key="`beneficialOwners_${member.id}`">
                                    <v-icon>person</v-icon>
                                    {{
                                        member.name
                                    }}{{ member.identification_code ? ` (${member.identification_code})` : '' }}
                                </v-col>
                            </v-col>

                        </v-row>
                    </v-sheet>
                    <v-sheet class="pa-8 mt-3 rounded" elevation="18">
                        <h1 class="title">{{ "Signing" | t }}</h1>
                        <v-form v-model="signerValidation">
                            <v-autocomplete
                                    v-model="company.signer"
                                    :label="'The person who signs the contract' | t"
                                    :rules="[v => !!v || this.t('Signer is required')]"
                                    @change="handleSignerChanged($event)"
                                    :items="signerSelectOptions"
                                    :loading="signerSelectOptions.length === 0"
                                    :placeholder="'Start typing to search signer' | t"
                                    :item-text="
                                        item => `${item.name}${item.identification_code ? ` (${item.identification_code})` : ''}`"
                                    item-value="id"
                                    :disabled="!!signingUrl"
                            />
                            <div v-if="showRepresentativeFields">
                                <v-text-field
                                        v-model="company.representative.name"
                                        :label="'Representative name' | t"
                                        :rules="[v => !!v || this.t('Representative name is required')]"
                                        placeholder="Mari-Liis Männik"
                                        prepend-icon="person"
                                        :disabled="!!signingUrl"
                                />
                                <v-select
                                        v-model="company.representative.country"
                                        :label="'Representative country' | t"
                                        :rules="[v => !!v || this.t('Representative country is required')]"
                                        :items="countries"
                                        item-text="text"
                                        item-value="value"
                                        prepend-icon="flag"
                                        :disabled="!!signingUrl"
                                />
                                <v-text-field
                                        v-model="company.representative.identification_code"
                                        :label="'Representative identification code' | t"
                                        :rules="[v => !!v || this.t('Representative identification code is required')]"
                                        placeholder="47101010033"
                                        prepend-icon="description"
                                        :disabled="!!signingUrl"
                                />
                                <v-file-input
                                        v-model="company.representative.proxy_file"
                                        :label="'Representative proxy file' | t"
                                        :rules="[v => !!v || this.t('Representative proxy file is required')]"
                                        placeholder="Allowed types: pdf, asice, bdoc, edoc"
                                        accept=".pdf, .asice, .bdoc, .edoc"
                                        chips
                                        show-size
                                        :disabled="!!signingUrl"
                                ></v-file-input>
                            </div>
                            <v-select
                                    v-model="company.has_politically_exposed_person"
                                    :label="'Are there people with a government background among those involved?' | t"
                                    class="mt-2"
                                    dense
                                    :items="politicalPersonItems"
                                    item-text="text"
                                    item-value="value"
                                    :disabled="!!signingUrl"
                            />
                            <v-checkbox v-model="company.agreed_terms"
                                        :rules="[v => !!v || this.t('The terms must be accepted.')]"
                                        :disabled="!!signingUrl"
                            >
                                <template v-slot:label>
                                    <div>
                                        {{ "I agree with" | t }}
                                        <v-tooltip bottom>
                                            <template v-slot:activator="{ on }">
                                                <a
                                                        v-on="on"
                                                        :href="`${url}/public/kasutustingimused/`"
                                                        target="_blank"
                                                        @click.stop
                                                >
                                                    {{ "Terms" | t }}
                                                </a>
                                            </template>
                                            {{ `Tarkpay ${t("terms of use")}` }}
                                        </v-tooltip>
                                    </div>
                                </template>
                            </v-checkbox>
                        </v-form>
                        <v-btn :disabled="!companyValidation || ! signerValidation || !!signingUrl" class="flex-row"
                               @click="handleSignContractClick()">
                            {{ !signingLoading ? t("Sign contract") : t("You will be redirected to signing...") }}
                        </v-btn>
                    </v-sheet>
                    <v-sheet class="pa-8 mt-3 rounded" elevation="18" v-if="signingUrl">
                        <v-row>
                            <dokobit-signing-window/>
                        </v-row>
                    </v-sheet>
                </v-col>
                <v-col justify="space-around" v-else-if="showError">
                    <v-sheet class="pa-8 rounded justify-center d-flex flex-column" elevation="18">
                        <v-icon x-large>error</v-icon>
                        <h2 class="title text-center">{{
                                "Oops! Looks like entered information is not correct!" | t
                            }}</h2>
                        <p class="text-center">
                            {{
                                "If you are sure that you entered right data then please contact with our administrator." | t
                            }}</p>
                    </v-sheet>
                </v-col>
                <v-col justify="space-around" v-else-if="showSuccess">
                    <v-sheet class="pa-8 rounded justify-center d-flex flex-column" elevation="18">
                        <v-icon x-large>check</v-icon>
                        <h2 class="title text-center">{{
                                "Your company is successfully registered" | t
                            }}</h2>
                        <p class="text-center">
                            {{
                                "We receive your application and now administrator must confirm it. You can close this tab now." | t
                            }}</p>
                    </v-sheet>
                </v-col>
            </v-row>
        </v-sheet>
    </v-container>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";
import DokobitSigningWindow from "@/components/dokobit/dokobit-signing-window.vue";

export default {
    name: "onboarding-view",
    components: {DokobitSigningWindow},
    data() {
        return {
            companyValidation: false,
            signerValidation: false,
            showRepresentativeFields: false,
            companyInputsDisabled: true,
            url: process.env.VUE_APP_URL,
            rules: {
                required: value => !!value || this.t("Required."),
            },
            politicalPersonItems: [
                {
                    text: this.t("No"),
                    value: false
                },
                {
                    text: this.t("Yes"),
                    value: true
                }
            ],
            countries: [
                {
                    text: "Estonia",
                    value: "EE"
                },
                {
                    text: "Latvia",
                    value: "LV"
                },
                {
                    text: "Lithuania",
                    value: "LT"
                }
            ],
            eIDEasyPinger: null,
        };
    },
    async mounted() {
        this.setShowError(false);
        this.setShowSuccess(false);
        this.url = process.env.VUE_APP_URL;
        //await this.logOutCurrentUser(false);
        const response = await this.getCompanyInformation({hash: this.$route?.params?.hash});
        if (!response) {
            this.setShowError(true);
        }
    },
    computed: {
        ...mapState('onboarding', ['company', 'loading', 'showSuccess', 'showError', 'signingUrl', 'notFilledFields', 'signingLoading']),
        membersOfTheBoard() {
            return this.company?.members?.filter(m => m.entity_type === 'member_of_the_board') || [];
        },
        beneficialOwners() {
            return this.company?.members?.filter(m => m.entity_type === 'beneficial_owner') || [];
        },
        signerSelectOptions() {
            let members = [...this.membersOfTheBoard];
            members.push({
                id: "representative",
                name: "Representative",
                type: "representative"
            })
            return members;
        }
    },
    methods: {
        ...mapActions(["logOutCurrentUser"]),
        ...mapMutations("onboarding", ["setLoading", "setShowError", "setShowSuccess", "setSigningLoading"]),
        ...mapActions("onboarding", ["getCompanyInformation", "updateCompanyInformationAndCreateSigningLink"]),
        async handleSignerChanged(value) {
            this.showRepresentativeFields = value && value === 'representative';
        },
        async handleSignContractClick() {
            if (this.company.signer) {
                const urlResponse = await this.updateCompanyInformationAndCreateSigningLink();
                if (urlResponse) {
                    this.setLoading(true);
                    this.setSigningLoading(true);
                    setTimeout(() => {
                        //window.scrollTo({top: document.body.scrollHeight, behavior: "smooth"});
                        window.location = urlResponse;
                        this.setLoading(false)

                    }, 1000)
                }
            }
        },
    }
};
</script>

<style scoped>
.subtitle {
    font-size: 16px;
}
</style>
